// components/agent-chat/components/VideoPlayer.jsx
import { useState, useRef } from 'react';
import { Film, AlertTriangle, Download, Play, Pause, Volume2, VolumeX } from 'lucide-react';

const VideoPlayer = ({ url, primaryColor, title = 'Vídeo' }) => {
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(true);
  const videoRef = useRef(null);

  const handleVideoError = (e) => {
    console.error('Erro ao carregar vídeo:', e);
    setError('Não foi possível carregar o vídeo. Verifique a URL ou tente novamente.');
  };

  const downloadVideo = () => {
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = title || 'video.mp4';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  // Se não houver URL, mostrar placeholder
  if (!url) {
    return (
      <div className="mt-3 border-t border-gray-200 pt-2">
        <div className="rounded-lg overflow-hidden bg-gray-100">
          <div className="flex flex-col items-center justify-center p-6 min-h-[150px]">
            <Film className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-500 text-center">Nenhum vídeo disponível</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-gray-200 pt-2">
      <div className="rounded-lg overflow-hidden bg-gray-100 relative group">
        {error ? (
          <div className="flex flex-col items-center justify-center p-6 min-h-[150px]">
            <AlertTriangle className="w-8 h-8 text-red-500 mb-2" />
            <p className="text-sm text-red-600 text-center">{error}</p>
            <button 
              onClick={() => {
                setError(null);
                if (videoRef.current) {
                  videoRef.current.load();
                }
              }}
              className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="relative">
            <video 
              ref={videoRef}
              className="w-full rounded-lg"
              controls
              playsInline
              src={url}
              onError={handleVideoError}
              onPlay={handlePlay}
              onPause={handlePause}
              onMouseEnter={() => setShowDownloadButton(true)}
              onMouseLeave={() => setShowDownloadButton(false)}
            >
              Seu navegador não suporta a reprodução de vídeos.
            </video>
            
            {/* Controles customizados opcionais - aparecem no hover */}
            <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {showDownloadButton && (
                <button
                  onClick={downloadVideo}
                  className="p-2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full transition-all"
                  title="Baixar vídeo"
                >
                  <Download className="w-4 h-4" />
                </button>
              )}
              
              <button
                onClick={toggleMute}
                className="p-2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full transition-all"
                title={isMuted ? "Ativar som" : "Desativar som"}
              >
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              
              <button
                onClick={togglePlayPause}
                className="p-2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full transition-all"
                title={isPlaying ? "Pausar" : "Reproduzir"}
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;